import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { documentApi, useGetInvestmentFundQuery } from 'api/main';
import { useSendRegulationAgreementMutation } from 'api/tunnelSteps';
import { userSelectors } from 'reducers';
import { useCheckInvestmentFund } from 'utils/hooks';
import { fundTypes, investmentFunds } from 'utils/constants';
import { downloadClick, isStatusOk } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import { SubmitButton, PDFViewer, Loader, CheckboxLabel } from 'components';
import TunnelStep from '../common/TunnelStep';
import TunnelBox from '../common/TunnelBox';
import { Box, Link, Typography } from '@mui/material';

const getDownloadRegulationText = (isSLP, isFPS) => {
    if (isSLP) return texts.DOWNLOAD_REGULATION_SLP;
    if (isFPS) return texts.DOWNLOAD_REGULATION_FPS;

    return texts.DOWNLOAD_REGULATION;
};

const getTunnelStepTitle = (isSLP, isFPS) => {
    if (isSLP) return texts.FUND_REGULATIONS_SLP;
    if (isFPS) return texts.FUND_REGULATIONS_FPS;

    return texts.FUND_REGULATIONS;
};

const getMustGoThroughRegulationText = (isSLP, isFPS) => {
    if (isSLP) return texts.MUST_GO_THROUGH_REGULATIONS_SLP;
    if (isFPS) return texts.MUST_GO_THROUGH_REGULATIONS_FPS;

    return texts.MUST_GO_THROUGH_REGULATIONS;
};

const getAcceptanceOfRegulationsText = (isSLP, isFPS) => {
    if (isSLP) return texts.ACCEPTANCE_OF_REGULATIONS_SLP;
    if (isFPS) return texts.ACCEPTANCE_OF_REGULATIONS_FPS;

    return texts.ACCEPTANCE_OF_REGULATIONS;
};

const FundRegulations = props => {
    const { handleNextStep, userStep } = props;

    const tunnelSubscription = useSelector(userSelectors.getTunnelSubscription);
    const isApax = useCheckInvestmentFund(investmentFunds.APAX);
    const isSLP = tunnelSubscription?.investmentFund?.type === fundTypes.SLP;
    const isFPS = tunnelSubscription?.investmentFund?.type === fundTypes.FPS;

    const isResubmit = useMemo(() => userStep?.successfulAttemptsNumber > 0, [userStep]);
    const hasStatusOk = useMemo(() => isStatusOk(userStep), [userStep]);

    const [sendRegulationAgreement, { isLoading: buttonLoader, isSuccess }] =
        useSendRegulationAgreementMutation();
    const { data: investmentFund, isFetching: loading } = useGetInvestmentFundQuery(
        tunnelSubscription?.investmentFund?.id
    );

    const [regulationAgreement, setRegulationAgreement] = useState(
        isResubmit ? tunnelSubscription?.fundRegulationAgreement : false
    );
    const [disclaimer, setDisclaimer] = useState(
        isResubmit ? tunnelSubscription?.fundRegulationAgreement : false
    );
    const [reachedBottom, setReachBottom] = useState(
        isResubmit ? tunnelSubscription?.fundRegulationAgreement : false
    );
    const [downloading, setDownloading] = useState(false);

    const fundRegulationUrl = investmentFund?.fundRegulation?.url;
    const fundRegulationDocName = investmentFund?.fundRegulation?.name;

    const handleDownloadDocument = useCallback(
        docId => {
            if (docId) {
                let mimeType = 'application/pdf';
                setDownloading(true);
                documentApi
                    .downloadDocumentById(docId)
                    .then(response => {
                        const bytes = new Uint8Array(response.data);
                        const blob = new Blob([bytes], { type: mimeType });
                        const url = window.URL.createObjectURL(blob);
                        downloadClick(url, investmentFund?.fundRegulation?.name);
                        setDownloading(false);
                    })
                    .catch(() => {
                        console.log('error downloading document');
                        setDownloading(false);
                    });
            }
        },
        [investmentFund]
    );

    const handleAgreementChange = () => setRegulationAgreement(prevState => !prevState);
    const handleDisclaimerChange = () => setDisclaimer(prevState => !prevState);

    const handleSubmit = useCallback(() => {
        sendRegulationAgreement(regulationAgreement);
    }, [sendRegulationAgreement, regulationAgreement]);

    useEffect(() => {
        if (isSuccess) {
            handleNextStep(true);
        }
    }, [isSuccess, handleNextStep]);

    return (
        <TunnelStep title={getTunnelStepTitle(isSLP, isFPS)} tunnelStepWidth={1100} maxWidth={1100}>
            <Box textAlign="center" p={1}>
                <Typography gutterBottom>{getMustGoThroughRegulationText(isSLP, isFPS)}</Typography>
            </Box>
            <TunnelBox
                sx={theme => ({ padding: '10px 0', backgroundColor: theme.palette.grey['A700'] })}
            >
                <Loader loading={loading}>
                    <PDFViewer url={fundRegulationUrl} setReachBottom={setReachBottom}></PDFViewer>
                </Loader>
            </TunnelBox>
            <Box textAlign="center" p={1}>
                <Loader loading={downloading} size={20} marginTop={0}>
                    <Link
                        href={fundRegulationUrl}
                        download={fundRegulationDocName}
                        target="_blank"
                        rel="noreferrer"
                        onClick={e => {
                            e.preventDefault();
                            handleDownloadDocument(investmentFund?.fundRegulation?.id);
                        }}
                        sx={theme => ({
                            color: theme.palette.common.black,
                            textDecorationColor: theme.palette.common.black,
                            '&:hover': { color: 'primary.main' },
                        })}
                    >
                        {getDownloadRegulationText(isSLP, isFPS)}
                    </Link>
                </Loader>
            </Box>
            <CheckboxLabel
                onChange={handleAgreementChange}
                value={regulationAgreement}
                checked={regulationAgreement}
                label={getAcceptanceOfRegulationsText(isSLP, isFPS)}
            />
            {isApax && (
                <CheckboxLabel
                    onChange={handleDisclaimerChange}
                    value={disclaimer}
                    checked={disclaimer}
                    label={texts.FUND_REGULATIONS_APAX}
                />
            )}
            <SubmitButton
                text={isResubmit ? texts.SUIVANT : texts.VALIDER}
                disabled={
                    !(regulationAgreement && reachedBottom && (isApax ? disclaimer : true)) ||
                    buttonLoader
                }
                onClick={hasStatusOk ? handleNextStep : handleSubmit}
                loading={buttonLoader}
            />
        </TunnelStep>
    );
};

export default FundRegulations;
